import { useCallback, useEffect, useMemo, useState } from "react";
import { get, useFormContext } from "react-hook-form";
import type { FieldError } from "react-hook-form";
import {
  type PasswordStrength,
  calculatePasswordStrength,
  isGreatPassword,
} from "@/react/helpers/passwordStrength";

interface UsePasswordFieldOptions {
  fieldName: string;
}

interface UsePasswordFieldReturn {
  handleBlur: () => Promise<void>;
  handleFocus: () => void;
  isFocused: boolean;
  isPasswordVisible: boolean;
  passwordFieldError: FieldError | undefined;
  passwordStrength: PasswordStrength;
  passwordValue: string | undefined;
  setIsPasswordVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

export const usePasswordField = ({
  fieldName,
}: UsePasswordFieldOptions): UsePasswordFieldReturn => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const {
    watch,
    formState: { errors },
    trigger,
  } = useFormContext();

  const passwordValue = watch(fieldName) as string | undefined;
  const passwordFieldError = get(errors, fieldName) as FieldError | undefined;

  const passwordStrength = useMemo<PasswordStrength>(
    () => calculatePasswordStrength(passwordValue || ""),
    [passwordValue],
  );

  useEffect(() => {
    if (!passwordValue) setIsPasswordVisible(false);
  }, [passwordValue]);

  useEffect(() => {
    if (isGreatPassword(passwordStrength) && passwordFieldError) {
      void trigger(fieldName);
    }
  }, [passwordStrength, passwordFieldError, trigger, fieldName]);

  const handleBlur = async () => {
    setIsFocused(false);
    await trigger(fieldName);
  };

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  return {
    isPasswordVisible,
    isFocused,
    setIsPasswordVisible,
    passwordFieldError,
    passwordValue,
    passwordStrength,
    handleBlur,
    handleFocus,
  };
};
